import type { AppConfig } from "../../core/types/config";
import type { WidgetEventMap } from "../../core/types/event";

/**
 * Parses URL search params into an initialAssets config object.
 *
 * Supports two formats:
 * - Legacy:  ?chains=fromId,toId&tokens=fromAddr,toAddr
 * - Pretty:  ?fromChain=ethereum&fromToken=eth&toChain=polygon&toToken=usdc
 *   - Collapsed chain/token: when both from/to chains or tokens are the same,
 *     they are collapsed into a single param:
 *       - ?chain=ethereum -> from and to chains are both ethereum
 *       - ?token=eth -> from and to tokens are both eth
 */
export const parseInitialAssetsFromUrl = (
  searchParams: URLSearchParams
): AppConfig["initialAssets"] => {
  // Legacy format
  if (searchParams.has("chains") || searchParams.has("tokens")) {
    const [fromTokenAddress, toTokenAddress] =
      searchParams.get("tokens")?.split(",") ?? [];
    const [fromChainId, toChainId] =
      searchParams.get("chains")?.split(",") ?? [];

    const from =
      fromChainId && fromTokenAddress
        ? { chainId: fromChainId, address: fromTokenAddress }
        : undefined;

    const to =
      toChainId && toTokenAddress
        ? { chainId: toChainId, address: toTokenAddress }
        : undefined;

    return { from, to };
  }

  // Pretty format
  const chainParam = searchParams.get("chain")?.toLowerCase();
  const tokenParam = searchParams.get("token")?.toLowerCase();
  const fromChainParam =
    searchParams.get("fromChain")?.toLowerCase() ?? chainParam;
  const toTokenExplicit = searchParams.get("toToken")?.toLowerCase();
  // ?chain=X falls back for toChain when toToken is explicit (same-chain bridge shorthand)
  const toChainParam =
    searchParams.get("toChain")?.toLowerCase() ??
    (toTokenExplicit ? chainParam : undefined);
  const fromTokenParam =
    searchParams.get("fromToken")?.toLowerCase() ?? tokenParam;
  const toTokenParam =
    toTokenExplicit ?? (toChainParam ? tokenParam : undefined);

  const from = fromChainParam
    ? { networkName: fromChainParam, symbol: fromTokenParam }
    : undefined;
  const to = toChainParam
    ? { networkName: toChainParam, symbol: toTokenParam }
    : undefined;

  return { from, to };
};

/**
 * Builds URL search params from a `swapParamsChanged` event detail.
 * Uses the pretty format, collapsing chain/token params when both sides match.
 */
export const buildUrlSearchParamsFromSwapEvent = (
  swapParams: WidgetEventMap["swapParamsChanged"]
): URLSearchParams => {
  const fromNetworkName = swapParams.fromNetworkName?.toLowerCase();
  const toNetworkName = swapParams.toNetworkName?.toLowerCase();
  const fromTokenSymbol = swapParams.fromTokenSymbol?.toLowerCase();
  const toTokenSymbol = swapParams.toTokenSymbol?.toLowerCase();

  const params = new URLSearchParams();
  const bothResolved =
    fromNetworkName && fromTokenSymbol && toNetworkName && toTokenSymbol;

  if (bothResolved) {
    if (fromNetworkName === toNetworkName) {
      params.set("chain", fromNetworkName);
    } else {
      params.set("fromChain", fromNetworkName);
      params.set("toChain", toNetworkName);
    }

    if (fromTokenSymbol === toTokenSymbol) {
      params.set("token", fromTokenSymbol);
    } else {
      params.set("fromToken", fromTokenSymbol);
      params.set("toToken", toTokenSymbol);
    }
  } else {
    if (fromNetworkName && fromTokenSymbol) {
      params.set("fromChain", fromNetworkName);
      params.set("fromToken", fromTokenSymbol);
    }
    if (toNetworkName && toTokenSymbol) {
      params.set("toChain", toNetworkName);
      params.set("toToken", toTokenSymbol);
    }
  }

  return params;
};
